import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "reactstrap";
import { InputField } from "../../Component/Common/InputField";
import { checkFormError } from "../../helper";
import { modalFileSchema } from "../../helper/errorSchema";
import { uploadExcelVoterApi } from "../../services/voterService";
import { invalidateVoterCache } from "./components/voterCache";

// The old "Upload Voters" modal from FullVoter.js, moved to its own routed
// page. Same validation, same API call (uploadExcelVoterApi -> POST
// voter/upload_full_voter_file), plus a progress bar and a working Cancel.

// --- Progress model -----------------------------------------------------------
//
// The upload is one request with two phases: sending the file (real byte
// progress from the browser) and the server importing the rows (no progress
// reported - typically several times longer than the send). One bar covers
// both: the send fills the first UPLOAD_SHARE of it, then the import phase
// is estimated and eases towards 99% so it keeps moving without ever
// claiming to be done; the server's response takes it to 100%.
//
// The import-time estimate learns: after each successful upload the
// observed server time per byte is saved (localStorage) and used for the
// next file, so the bar matches this server more closely over time. With
// nothing learned yet it assumes the import takes DEFAULT_PROCESSING_RATIO
// times as long as the send did (11.6s send vs 1.1min import observed).
const UPLOAD_SHARE = 15;
const DEFAULT_PROCESSING_RATIO = 5.5;
const MIN_PROCESSING_ESTIMATE_MS = 5000;
const RATE_STORAGE_KEY = "electioneasy_upload_ms_per_byte";

const readLearnedRate = () => {
  try {
    const value = Number(localStorage.getItem(RATE_STORAGE_KEY));
    return value > 0 ? value : null;
  } catch {
    return null;
  }
};

const saveLearnedRate = (msPerByte) => {
  if (!(msPerByte > 0)) return;
  try {
    const previous = readLearnedRate();
    // Blend with the previous value so one odd upload doesn't swing it.
    localStorage.setItem(RATE_STORAGE_KEY, String(previous ? (previous + msPerByte) / 2 : msPerByte));
  } catch {
    // Storage unavailable - just keep using the default ratio.
  }
};

const formatBytes = (bytes) => {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDuration = (ms) => {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes ? `${minutes}m ${String(seconds).padStart(2, "0")}s` : `${seconds}s`;
};

// Pure: turns the raw timings into what the bar shows.
const getProgressView = (upload, now) => {
  const { loaded, total, startedAt, sentAt, estimateMs, done } = upload;
  const elapsed = now - startedAt;
  if (done) return { percent: 100, stage: "done", elapsed };
  if (!sentAt) {
    const fraction = total ? Math.min(1, loaded / total) : 0;
    return { percent: Math.floor(fraction * UPLOAD_SHARE), stage: "sending", elapsed };
  }
  const processingMs = now - sentAt;
  // ~90% of the import segment at the estimate, then creeping towards 99%.
  const fraction = 1 - Math.exp((-2.3 * processingMs) / estimateMs);
  const percent = Math.min(99, Math.floor(UPLOAD_SHARE + (100 - UPLOAD_SHARE) * fraction));
  return { percent, stage: "processing", elapsed, remainingMs: estimateMs - processingMs };
};

const UploadProgress = ({ upload, now }) => {
  const { percent, stage, elapsed, remainingMs } = getProgressView(upload, now);
  const title =
    stage === "sending"
      ? "Uploading file…"
      : stage === "processing"
      ? "Importing voters on the server…"
      : "Upload complete";
  return (
    <div className="upload-progress" role="status" aria-live="polite">
      <div className="upload-progress-head">
        <span className="upload-progress-title">{title}</span>
        <span className="upload-progress-percent">{percent}%</span>
      </div>
      <div className={`upload-progress-track ${stage === "processing" ? "is-processing" : ""}`}>
        <div className="upload-progress-fill" style={{ width: `${percent}%` }} />
      </div>
      <div className="upload-progress-meta">
        {stage === "sending" ? (
          <span>
            Uploaded {formatBytes(upload.loaded)} of {formatBytes(upload.total)} · Remaining{" "}
            {formatBytes(Math.max(upload.total - upload.loaded, 0))}
          </span>
        ) : (
          <span>
            File sent ({formatBytes(upload.total)}) ·{" "}
            {stage === "done"
              ? "voters imported"
              : remainingMs > 0
              ? `about ${formatDuration(remainingMs)} left`
              : "taking longer than usual, almost done…"}
          </span>
        )}
        <span>Elapsed {formatDuration(elapsed)}</span>
      </div>
    </div>
  );
};

export const UploadVoter = () => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState({});
  const [inputError, setInputError] = useState({});
  const [isLoadding, setIsLoadding] = useState(false);
  // Timings of the running upload (see getProgressView); null when idle.
  const [upload, setUpload] = useState(null);
  const [now, setNow] = useState(Date.now());
  const abortRef = useRef(null);

  const location = useLocation();
  // Return to the page that opened this (e.g. the Dashboard's Upload button),
  // falling back to Voter Search when reached directly from the sidebar.
  const goBackToSearch = () => navigate(location.state?.from || "/voter-management/search");

  // Tick while uploading so the estimated import phase keeps moving.
  useEffect(() => {
    if (!isLoadding) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [isLoadding]);

  const handleOnChange = async (name, value) => {
    const stateObj = { ...inputValue, [name]: value };
    setInputValue(stateObj);
    const error = await checkFormError(stateObj, modalFileSchema);
    setInputError(error);
  };

  const startUpload = async (file) => {
    const controller = new AbortController();
    abortRef.current = controller;
    const startedAt = Date.now();
    const size = file?.size || 0;
    let sentAt = null;

    setIsLoadding(true);
    setNow(startedAt);
    setUpload({ loaded: 0, total: size, startedAt, sentAt: null, estimateMs: 0, done: false });

    const onUploadProgress = ({ loaded, total }) => {
      const fileTotal = total || size;
      const timestamp = Date.now();
      if (!sentAt && fileTotal && loaded >= fileTotal) {
        sentAt = timestamp;
        const learned = readLearnedRate();
        const estimateMs = Math.max(
          MIN_PROCESSING_ESTIMATE_MS,
          learned ? learned * fileTotal : (sentAt - startedAt) * DEFAULT_PROCESSING_RATIO
        );
        setUpload((current) => current && { ...current, loaded: fileTotal, total: fileTotal, sentAt, estimateMs });
      } else if (!sentAt) {
        setUpload((current) => current && { ...current, loaded, total: fileTotal });
      }
      setNow(timestamp);
    };

    const formData = new FormData();
    formData.append("excel_file", file);
    const res = await uploadExcelVoterApi(formData, { onUploadProgress, signal: controller.signal });

    // Cancelled: handleCancelUpload already reset the UI.
    if (controller.signal.aborted) return;
    abortRef.current = null;

    if (res?.success) {
      if (sentAt && size) saveLearnedRate((Date.now() - sentAt) / size);
      setUpload((current) => current && { ...current, done: true });
      setNow(Date.now());
      invalidateVoterCache(); // new voters - next visit reloads the list
      toast.success(res?.results?.success || "success");
      setIsLoadding(false);
      goBackToSearch();
    } else {
      // Server-side errors are already toasted by commonErrorHandler; a
      // network failure comes back as an Error with nothing shown yet.
      if (res instanceof Error) toast.error("Upload failed. Check your connection and try again.");
      setIsLoadding(false);
      setUpload(null);
    }
  };

  const onSubmitUpload = async () => {
    const validationResult = await checkFormError(
      { excel_file: inputValue.excel_file },
      modalFileSchema
    );
    if (validationResult === true) {
      startUpload(inputValue?.excel_file);
    } else {
      setInputError(validationResult);
    }
  };

  // Stops the request. If the file had already been fully sent, the server
  // may still finish importing it - cancelling only stops us waiting.
  const handleCancelUpload = () => {
    const controller = abortRef.current;
    if (!controller) return;
    const wasSent = !!upload?.sentAt;
    controller.abort();
    abortRef.current = null;
    setIsLoadding(false);
    setUpload(null);
    if (wasSent) {
      invalidateVoterCache();
      toast.warn("Upload cancelled. The file was already sent, so the server may still import it.");
    } else {
      toast.info("Upload cancelled");
    }
  };

  const handleClose = () => {
    if (isLoadding) handleCancelUpload();
    goBackToSearch();
  };

  return (
    <Modal isOpen toggle={isLoadding ? undefined : goBackToSearch} centered className="avf-dialog" contentClassName="avf-content">
      <div className="avf-header">
        <div className="avf-header-top">
          <div>
            <h2 className="avf-title">Upload Voters</h2>
            <p className="avf-subtitle">Bulk-add voters from an Excel file</p>
          </div>
          <button
            type="button"
            className="avf-close-btn"
            onClick={handleClose}
            aria-label={isLoadding ? "Cancel upload and close" : "Close"}
          >
            ×
          </button>
        </div>
      </div>

      <div className="avf-body">
        <div className="avf-section">
          <div className="avf-section-head">
            <span className="avf-section-num">1</span>
            <h3 className="avf-section-title">Excel File</h3>
          </div>
          <div className="avf-grid">
            <div className="avf-field">
              <InputField
                className="modal_input"
                label="File"
                labelClass="modal_label"
                labelSpan="*"
                labelSpanClass="style_for_star"
                placeholder="Select File"
                name="excel_file"
                type="file"
                disabled={isLoadding}
                focus={!!inputError.excel_file}
                error={inputError.excel_file}
                onChange={({ target: { name, files } }) => handleOnChange(name, files[0])}
              />
            </div>
          </div>
          {upload && <UploadProgress upload={upload} now={now} />}
        </div>
      </div>

      <div className="avf-footer">
        <div className={`avf-footer-note ${inputError?.excel_file ? "has-errors" : ""}`}>
          {inputError?.excel_file || "1 required field"}
        </div>
        <div className="avf-footer-actions">
          {isLoadding ? (
            <button type="button" className="avf-btn avf-btn-secondary" onClick={handleCancelUpload}>
              Cancel Upload
            </button>
          ) : (
            <button type="button" className="avf-btn avf-btn-secondary" onClick={goBackToSearch}>
              Cancel
            </button>
          )}
          {isLoadding ? (
            <button type="button" className="avf-btn avf-btn-primary" disabled>
              Processing...
            </button>
          ) : (
            <button
              type="button"
              className="avf-btn avf-btn-primary"
              disabled={!inputValue?.excel_file}
              onClick={onSubmitUpload}
            >
              Upload
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};
